import {
  AlertasDeReservasSchema,
  AsignacionDeExportacionSchema,
  CambioDeReservaSchema,
  DetalleDeReservaSchema,
  LecturaDePdfSchema,
  ListaDeReservasSchema,
  ReservaSchema,
  ResultadoDeAsignacionSchema,
  ResultadoDeConsultaDeReservaSchema,
  RevisionDeVinculoSchema,
  type EdicionDeReserva,
  type GuardarReserva,
  type VinculoDeReserva,
} from '@cnc/contracts';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {z} from 'zod';
import {json, pedir} from './cliente';
import {refrescarDatos} from './refrescar';

// Todo lo que cambia con una reserva o una asignacion: se refresca junto.
function useRefrescar() {
  const cliente = useQueryClient();
  return () => refrescarDatos(cliente);
}

export function useReservas(estado: 'activas' | 'todas', buscar: string) {
  const parametros = new URLSearchParams({estado});
  if (buscar.trim()) parametros.set('buscar', buscar.trim());
  return useQuery({
    queryKey: ['reservas', 'lista', parametros.toString()],
    queryFn: () => pedir(`reservas?${parametros.toString()}`, ListaDeReservasSchema),
  });
}

export function useDetalleDeReserva(id: number | null) {
  return useQuery({
    queryKey: ['reservas', 'detalle', id],
    queryFn: () => pedir(`reservas/${id}`, DetalleDeReservaSchema),
    enabled: id !== null,
  });
}

export function useAlertasDeReservas(activo = true) {
  return useQuery({
    enabled: activo,
    queryKey: ['reservas', 'alertas'],
    queryFn: () => pedir('reservas/alertas', AlertasDeReservasSchema),
    refetchInterval: 5 * 60_000,
  });
}

export function useLeerPdfDeReserva() {
  return useMutation({
    mutationFn: (archivo: File) => {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo, archivo.name);
      return pedir('reservas/leer-pdf', LecturaDePdfSchema, {method: 'POST', body: cuerpo});
    },
  });
}

export function useGuardarReserva() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (cuerpo: GuardarReserva) => pedir('reservas', ReservaSchema, json('POST', cuerpo)),
    onSuccess: refrescar,
  });
}

export function useEditarReserva() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: ({id, cambios}: {id: number; cambios: EdicionDeReserva}) =>
      pedir(`reservas/${id}`, ReservaSchema, json('PATCH', cambios)),
    onSuccess: refrescar,
  });
}

export function useVincularReserva() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: ({
      id,
      exportacion,
      valores,
      minas,
    }: {
      id: number;
      exportacion: string | null;
      valores?: VinculoDeReserva['valores'];
      minas?: number[];
    }) => pedir(`reservas/${id}/exportacion`, ReservaSchema, json('POST', {exportacion, valores, minas})),
    onSuccess: refrescar,
  });
}

// Eliminar una reserva (solo administradores).
export function useEliminarReserva() {
  const refrescar = useRefrescar();
  return useMutation({
    // La API responde 204 sin cuerpo.
    mutationFn: (id: number) => pedir(`reservas/${id}`, z.null(), json('DELETE')),
    onSuccess: refrescar,
  });
}

// Antes de vincular: si la exportacion existe, sus datos frente a los de la reserva.
export function useRevisarVinculo() {
  return useMutation({
    mutationFn: ({id, exportacion}: {id: number; exportacion: string}) =>
      pedir(`reservas/${id}/exportacion/revisar`, RevisionDeVinculoSchema, json('POST', {exportacion})),
  });
}

export function useConsultarNavieraDeReserva() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) =>
      pedir(`reservas/${id}/consultar-naviera`, ResultadoDeConsultaDeReservaSchema, json('POST')),
    onSuccess: refrescar,
  });
}

export function useMarcarCambioRevisado() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (cambioId: number) =>
      pedir(`reservas/cambios/${cambioId}/revisado`, CambioDeReservaSchema, json('POST')),
    onSuccess: refrescar,
  });
}

export const rutaDelPdfDeReserva = (id: number) => `/api/reservas/${id}/pdf`;

// ── Contenedores de una exportacion ─────────────────────────────────────────

const rutaDeAsignacion = (codigo: string) => `seguimiento/exportaciones/${encodeURIComponent(codigo)}/asignacion`;

export function useAsignacion(codigo: string) {
  return useQuery({
    queryKey: ['asignacion', codigo],
    queryFn: () => pedir(rutaDeAsignacion(codigo), AsignacionDeExportacionSchema),
  });
}

type Ajuste =
  | {tipo: 'asignar'}
  | {tipo: 'automatica'; activa: boolean}
  | {tipo: 'agregar'; contenedorId: number; bbNo: string}
  | {tipo: 'quitar'; bbNo: string}
  | {tipo: 'liberar'; contenedorId: number}
  | {tipo: 'numero'; contenedorId: number; numero: string}
  | {tipo: 'minas'; minas: number[]};

export function useAjustarAsignacion(codigo: string) {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: async (ajuste: Ajuste): Promise<string | null> => {
      const base = rutaDeAsignacion(codigo);
      switch (ajuste.tipo) {
        case 'asignar':
          return (await pedir(`${base}/asignar`, ResultadoDeAsignacionSchema, json('POST'))).mensaje;
        case 'automatica':
          await pedir(`${base}/automatica`, AsignacionDeExportacionSchema, json('POST', {activa: ajuste.activa}));
          return null;
        case 'agregar':
          await pedir(
            `${base}/contenedores/${ajuste.contenedorId}/bolsas`,
            AsignacionDeExportacionSchema,
            json('POST', {bbNo: ajuste.bbNo}),
          );
          return null;
        case 'quitar':
          await pedir(
            `${base}/bolsas/${encodeURIComponent(ajuste.bbNo)}`,
            AsignacionDeExportacionSchema,
            json('DELETE'),
          );
          return null;
        case 'liberar':
          await pedir(`${base}/contenedores/${ajuste.contenedorId}`, AsignacionDeExportacionSchema, json('DELETE'));
          return null;
        case 'numero':
          await pedir(
            `${base}/contenedores/${ajuste.contenedorId}`,
            AsignacionDeExportacionSchema,
            json('PATCH', {numero: ajuste.numero}),
          );
          return null;
        case 'minas':
          await pedir(`${base}/minas`, AsignacionDeExportacionSchema, json('PUT', {minas: ajuste.minas}));
          return null;
      }
    },
    onSuccess: refrescar,
  });
}
